import { ProtocolRule, ProtocolAction } from '../types/protocol'
import './ProtocolRulesEditor.css'

const ACTION_TYPES: { value: ProtocolAction['type']; label: string }[] = [
  { value: 'create-task', label: 'Create Task' },
  { value: 'send-alert', label: 'Send Alert' },
  { value: 'schedule-appointment', label: 'Schedule Appointment' },
  { value: 'prescribe-medication', label: 'Prescribe Medication' },
  { value: 'order-test', label: 'Order Test' },
  { value: 'refer', label: 'Refer' },
]

interface ProtocolRulesEditorProps {
  rules: ProtocolRule[]
  onChange: (rules: ProtocolRule[]) => void
}

function newRule(): ProtocolRule {
  return {
    id: crypto.randomUUID(),
    name: '',
    description: '',
    criteria: [],
    actions: [],
    priority: 0,
  }
}

function newAction(): ProtocolAction {
  return {
    type: 'create-task',
    title: '',
    dueDateOffset: undefined,
  }
}

export function ProtocolRulesEditor({ rules, onChange }: ProtocolRulesEditorProps) {
  const addRule = () => {
    const newRules = [...rules, newRule()]
    // Set priority based on position
    newRules.forEach((r, i) => {
      r.priority = i
    })
    onChange(newRules)
  }

  const updateRule = (index: number, updates: Partial<ProtocolRule>) => {
    const newRules = [...rules]
    newRules[index] = { ...newRules[index], ...updates }
    onChange(newRules)
  }

  const removeRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index)
    newRules.forEach((r, i) => {
      r.priority = i
    })
    onChange(newRules)
  }

  const addAction = (ruleIndex: number) => {
    const newRules = [...rules]
    const rule = newRules[ruleIndex]
    rule.actions = [...(rule.actions || []), newAction()]
    onChange(newRules)
  }

  const updateAction = (ruleIndex: number, actionIndex: number, updates: Partial<ProtocolAction>) => {
    const newRules = [...rules]
    const rule = newRules[ruleIndex]
    const actions = [...(rule.actions || [])]
    actions[actionIndex] = { ...actions[actionIndex], ...updates }
    rule.actions = actions
    onChange(newRules)
  }

  const removeAction = (ruleIndex: number, actionIndex: number) => {
    const newRules = [...rules]
    const rule = newRules[ruleIndex]
    rule.actions = (rule.actions || []).filter((_, i) => i !== actionIndex)
    onChange(newRules)
  }

  const moveRule = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= rules.length) return
    const newRules = [...rules]
    ;[newRules[index], newRules[newIndex]] = [newRules[newIndex], newRules[index]]
    newRules.forEach((r, i) => {
      r.priority = i
    })
    onChange(newRules)
  }

  return (
    <div className="protocol-rules-editor">
      <div className="rules-editor-header">
        <h3>Rules</h3>
        <p className="rules-editor-help">
          Rules define actions to be performed for enrolled patients. Actions are executed in priority order.
        </p>
        <button type="button" className="btn-add-rule" onClick={addRule}>
          + Add Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="rules-empty">
          <p>No rules yet. Add a rule to define protocol actions (e.g. create tasks, schedule appointments).</p>
        </div>
      ) : (
        <div className="rules-list">
          {rules.map((rule, ruleIndex) => (
            <div key={rule.id} className="rule-editor-card">
              <div className="rule-editor-header">
                <span className="rule-ordinal">Rule {ruleIndex + 1}</span>
                <div className="rule-editor-actions">
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => moveRule(ruleIndex, 'up')}
                    disabled={ruleIndex === 0}
                    title="Move up"
                    aria-label="Move rule up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => moveRule(ruleIndex, 'down')}
                    disabled={ruleIndex === rules.length - 1}
                    title="Move down"
                    aria-label="Move rule down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeRule(ruleIndex)}
                    title="Remove rule"
                    aria-label="Remove rule"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="rule-fields">
                <div className="form-group">
                  <label htmlFor={`rule-name-${rule.id}`}>
                    Rule Name <span className="required">*</span>
                  </label>
                  <input
                    id={`rule-name-${rule.id}`}
                    type="text"
                    value={rule.name}
                    onChange={(e) => updateRule(ruleIndex, { name: e.target.value })}
                    placeholder="e.g., Annual Review"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`rule-desc-${rule.id}`}>Description</label>
                  <textarea
                    id={`rule-desc-${rule.id}`}
                    value={rule.description || ''}
                    onChange={(e) => updateRule(ruleIndex, { description: e.target.value })}
                    placeholder="Describe when this rule applies"
                    rows={2}
                  />
                </div>

                <div className="actions-section">
                  <div className="actions-section-header">
                    <label>Actions</label>
                    <button
                      type="button"
                      className="btn-add-action"
                      onClick={() => addAction(ruleIndex)}
                    >
                      + Add Action
                    </button>
                  </div>

                  {(!rule.actions || rule.actions.length === 0) ? (
                    <p className="actions-empty">No actions. Add an action (e.g. create task, schedule appointment).</p>
                  ) : (
                    <div className="actions-list">
                      {rule.actions.map((action, actionIndex) => (
                        <div key={actionIndex} className="action-row">
                          <select
                            value={action.type}
                            onChange={(e) =>
                              updateAction(ruleIndex, actionIndex, {
                                type: e.target.value as ProtocolAction['type'],
                              })
                            }
                            aria-label="Action type"
                          >
                            {ACTION_TYPES.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={action.title}
                            onChange={(e) =>
                              updateAction(ruleIndex, actionIndex, { title: e.target.value })
                            }
                            placeholder="Action title (e.g. Book annual review)"
                            className="action-title-input"
                            required
                          />
                          <input
                            type="number"
                            min={0}
                            value={action.dueDateOffset ?? ''}
                            onChange={(e) => {
                              const v = e.target.value
                              updateAction(ruleIndex, actionIndex, {
                                dueDateOffset: v === '' ? undefined : parseInt(v, 10),
                              })
                            }}
                            placeholder="Days"
                            className="action-days-input"
                            title="Days from enrollment"
                            aria-label="Due days from enrollment"
                          />
                          <span className="action-days-label">days from enrollment</span>
                          <button
                            type="button"
                            className="btn-remove-action"
                            onClick={() => removeAction(ruleIndex, actionIndex)}
                            aria-label="Remove action"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
